import React, { PropsWithChildren, Children } from 'react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { CheckCircleSolidIcon } from '@fiverr-private/visuals';
import { Types } from '@fiverr-private/theme';

type CheckboxSize = '16px' | '24px';

export interface ProgressBarProps {
  type: 'numeric' | 'checkbox';
  currentStep?: number;
  checkboxSize: CheckboxSize;
  connectingLine: boolean;
}

const lineVerticalGap = '2px';

const ProgressBarIcon = ({
  index,
  currentStep,
  amountOfSteps,
  checkboxSize = '16px',
}: {
  checkboxSize: CheckboxSize;
  index: number;
  currentStep: number;
  amountOfSteps: number;
}) => {
  const iconSize = checkboxSize === '24px' ? 'lg' : 'sm';

  if (index > currentStep) {
    return null;
  }

  if (index < currentStep || currentStep === amountOfSteps) {
    return (
      <Container width={checkboxSize} height={checkboxSize} backgroundColor="white">
        <CheckCircleSolidIcon color="black" size={iconSize} />
      </Container>
    );
  }

  if (index === currentStep) {
    return (
      <Container
        display="flex"
        justifyContent="center"
        alignItems="center"
        borderRadius="circle"
        width={checkboxSize}
        height={checkboxSize}
        backgroundColor="grey_1200"
      >
        <Container borderRadius="circle" width="4px" height="4px" backgroundColor="white" />
      </Container>
    );
  }

  return null;
};

export const ProgressBar = ({
  type,
  currentStep = 0,
  checkboxSize = '24px',
  connectingLine = true,
  children,
}: PropsWithChildren<ProgressBarProps>) => {
  const amountOfSteps = Children.toArray(children).length - 1;
  const getLineProps = (index: number): Types.ContainerProps => {
    const isComplete = index < currentStep;
    if (index !== amountOfSteps) {
      return {
        position: 'absolute',
        display: 'block',
        width: '2px',
        bottom: '0px',
        backgroundColor: isComplete ? 'grey_1200' : 'grey_500',
      };
    }

    return { backgroundColor: isComplete ? 'grey_1200' : undefined };
  };

  return (
    <Stack direction="column" gap="0">
      {Children.map(children, (child, index) => (
        <Stack position="relative" gap="0">
          {connectingLine && (
            <Container
              {...getLineProps(index)}
              style={{
                // 1px is line width / 2
                left: `calc((${checkboxSize} / 2) - 1px)`,
                top: `calc(${checkboxSize} + ${lineVerticalGap})`,
                bottom: lineVerticalGap,
              }}
            />
          )}
          <Container
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexShrink={0}
            width={checkboxSize}
            height={checkboxSize}
            backgroundColor={index <= currentStep ? 'grey_1200' : 'grey_500'}
            color="white"
            borderRadius="circle"
            fontWeight="bold"
            fontSize="b_sm"
          >
            {type === 'numeric' ? (
              index + 1
            ) : (
              <ProgressBarIcon
                index={index}
                amountOfSteps={amountOfSteps}
                currentStep={currentStep}
                checkboxSize={checkboxSize}
              />
            )}
          </Container>
          {child}
        </Stack>
      ))}
    </Stack>
  );
};
